<template>
  <q-dialog v-model="show" :maximized="$q.screen.lt.sm" persistent>
    <q-card :style="$q.screen.gt.sm ? 'min-width: 900px; max-width: 95vw;' : 'width: 100vw;'">
      <q-card-section>
        <div class="row items-center justify-between">
          <div class="row items-center">
            <div class="text-h6">AI Reports</div>
            <q-badge class="q-ml-sm" color="primary" text-color="white">Beta</q-badge>
          </div>
        </div>
        <div style="font-size: 12px">
          {{
            mode === 'template'
              ? 'Select a template and generate a new report'
              : 'Manually select and order sections for your report'
          }}
        </div>
      </q-card-section>
      <q-separator v-if="!reportContent.length" />
      <q-card-section v-if="!reportContent.length">
        <q-btn-toggle
          v-model="mode"
          :options="[
            { label: 'Use Template', value: 'template' },
            { label: 'Custom Report', value: 'custom' }
          ]"
          class="q-mb-lg"
          no-caps
          spread
          style="outline: 1px solid grey"
          toggle-color="primary"
          unelevated
        />

        <div v-if="loading" class="text-center q-pa-md">
          <q-spinner color="primary" size="2em" />
          <div class="text-grey-6 q-mt-sm">Loading templates...</div>
        </div>

        <div v-else-if="error" class="text-center q-pa-md">
          <q-icon color="negative" name="error" size="2em" />
          <div class="text-negative q-mt-sm">{{ error }}</div>
          <q-btn class="q-mt-sm" color="primary" flat label="Retry" @click="loadTemplates" />
        </div>

        <div v-else-if="generating" class="text-center q-pa-md">
          <q-spinner color="primary" size="2em" />
          <div class="text-grey-6 q-mt-sm">Generating report...</div>
          <div class="text-caption text-grey-5 q-mt-xs">
            This may take a few moments (1-3 minutes)
          </div>
        </div>

        <div v-else-if="mode === 'template'">
          <q-select
            v-model="selectedTemplate"
            :options="templates"
            :rules="[(val) => !!val || 'Please select a template']"
            class="q-mb-md"
            hide-bottom-space
            label="Select Template"
            option-label="name"
            option-value="id"
            outlined
            placeholder="Choose a report template"
          >
            <template #selected-item="scope">
              <div v-if="scope.opt" class="row items-center no-wrap justify-between full-width">
                <div class="q-mr-sm">{{ scope.opt.name }}</div>
                <div v-if="scope.opt.tags && scope.opt.tags.length" class="row q-gutter-xs">
                  <q-chip
                    v-for="(tag, index) in scope.opt.tags"
                    :key="index"
                    class="bg-primary text-white"
                    outline
                    size="sm"
                    square
                    style="font-size: 11px"
                  >
                    {{ tag }}
                  </q-chip>
                </div>
              </div>
            </template>
            <template #option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section>
                  <q-item-label>{{ scope.opt.name }}</q-item-label>
                </q-item-section>
                <q-item-section v-if="scope.opt.tags && scope.opt.tags.length" side>
                  <div class="row q-gutter-xs">
                    <q-chip
                      v-for="(tag, index) in scope.opt.tags"
                      :key="index"
                      class="bg-primary text-white"
                      outline
                      size="sm"
                      square
                      style="font-size: 11px"
                    >
                      {{ tag }}
                    </q-chip>
                  </div>
                </q-item-section>
              </q-item>
            </template>
            <template #no-option>
              <q-item>
                <q-item-section class="text-grey"> No templates available </q-item-section>
              </q-item>
            </template>
          </q-select>
        </div>

        <div v-else-if="mode === 'custom'" class="row">
          <div class="col-12">
            <div class="row items-center justify-between q-mb-sm">
              <div class="text-subtitle2">Report Sections (Select and Drag to re-order)</div>
              <q-btn
                color="primary"
                flat
                icon="add_a_photo"
                label="Add Image"
                no-caps
                @click="triggerImageUpload"
              />
              <input
                ref="imageInput"
                accept="image/*"
                style="display: none"
                type="file"
                @change="onImageSelected"
              />
            </div>
            <q-scroll-area
              :style="{
                height: $q.screen.gt.sm ? '500px' : '40vh',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }"
            >
              <draggable
                v-model="customSections"
                :style="{ minHeight: $q.screen.gt.sm ? '480px' : '38vh' }"
                class="list-group q-pa-sm"
                item-key="key"
              >
                <template #item="{ element }">
                  <q-item
                    :class="element.selected ? 'bg-primary text-white' : ''"
                    class="q-mb-xs rounded-borders cursor-move"
                    clickable
                    @click="element.selected = !element.selected"
                  >
                    <q-item-section avatar side>
                      <q-icon
                        :color="element.selected ? 'white' : 'grey-7'"
                        name="drag_indicator"
                        size="xs"
                      />
                    </q-item-section>
                    <q-item-section v-if="element.type === 'image'" avatar>
                      <q-img
                        :src="`data:${element.mimeType};base64,${element.data}`"
                        class="rounded-borders"
                        style="width: 50px; height: 50px"
                      />
                    </q-item-section>
                    <q-item-section>{{ element.label }}</q-item-section>
                    <q-item-section side>
                      <div class="row items-center">
                        <q-btn
                          v-if="element.type === 'image'"
                          :color="element.selected ? 'white' : 'negative'"
                          dense
                          flat
                          icon="delete"
                          size="sm"
                          @click.stop="removeImage(element)"
                        />
                        <span
                          v-if="
                            element.key === 'google_maps_image_alternative' &&
                            mapSnapshotAlternativeUrl.length > 0
                          "
                          class="cursor-pointer text-underline"
                        >
                          Preview
                          <q-tooltip
                            v-if="mapSnapshotAlternativeUrl"
                            anchor="center right"
                            self="center left"
                            :offset="[10, 0]"
                          >
                            <img
                              :src="mapSnapshotAlternativeUrl"
                              style="max-width: 350px; max-height: 250px; border-radius: 4px"
                            />
                          </q-tooltip>
                        </span>
                        <span
                          v-if="
                            element.key === 'google_maps_image_static' && mapSnapshotUrl.length > 0
                          "
                          class="cursor-pointer text-underline"
                        >
                          Preview
                          <q-tooltip
                            v-if="mapSnapshotUrl"
                            anchor="center right"
                            self="center left"
                            :offset="[10, 0]"
                          >
                            <img
                              :src="mapSnapshotUrl"
                              style="max-width: 350px; max-height: 250px; border-radius: 4px"
                            />
                          </q-tooltip>
                        </span>
                        <q-toggle
                          v-model="element.selected"
                          :dark="element.selected"
                          color="white"
                        />
                      </div>
                    </q-item-section>
                  </q-item>
                </template>
              </draggable>
            </q-scroll-area>

            <div
              v-if="selectedSectionsCount === 0"
              class="text-caption text-negative q-mt-xs q-mb-md"
            >
              <q-icon class="q-mr-xs" name="warning" size="xs" />
              At least one section must be selected.
            </div>

            <div class="q-mt-md">
              <div class="text-subtitle2 q-mb-xs">Supplemental Documents (Optional)</div>
              <q-banner v-if="hasExcelAttachments" class="bg-blue-1 rounded-borders q-mb-sm" dense>
                <template #avatar>
                  <q-icon color="blue-9" name="info" size="sm" />
                </template>
                <div class="text-caption text-blue-9">
                  Excel files with multiple tables, or files with hidden sheets, rows, or columns,
                  may not work as expected.
                </div>
              </q-banner>
              <q-file
                v-model="attachmentsFiles"
                :max-file-size="512000"
                :max-files="3"
                accept=".pdf,.html,.docx,.pptx,.txt,.xlsx,.xlsm"
                append
                filled
                label="Attach supplemental documents (Max 3 files, 500KB each)"
                multiple
                use-chips
                @rejected="onAttachmentRejected"
              >
                <template #prepend>
                  <q-icon name="attach_file" />
                </template>
              </q-file>
            </div>
          </div>
        </div>
      </q-card-section>
      <q-editor
        v-if="reportContent.length > 0"
        v-model="reportContent"
        :disable="reportContent.length === 0 || generating"
        :max-height="$q.screen.gt.sm ? '500px' : '60vh'"
        :definitions="editorDefinitions"
        :toolbar="[
          ['left', 'center', 'right', 'justify'],
          ['bold', 'italic', 'underline', 'strike'],
          ['undo', 'redo'],
          ['print_with_images', 'fullscreen']
        ]"
        min-height="5rem"
      />
      <q-separator v-if="reportContent.length === 0" />
      <q-card-actions align="right">
        <q-btn flat label="Cancel" no-caps @click="cancel" />
        <q-btn
          v-if="reportContent.length === 0"
          id="property-report-ai_generate"
          :disable="
            (mode === 'template' && !selectedTemplate) ||
            (mode === 'custom' && selectedSectionsCount === 0) ||
            generating ||
            !hasSimilarities
          "
          :loading="generating"
          color="primary"
          label="Generate"
          no-caps
          unelevated
          @click="generate"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
import templater from '@/api/aireports.ts'
import { useUserStore } from '@/stores/user'
import draggable from 'vuedraggable'
import { REPORT_SECTIONS } from 'common/src/reportSections'

import type {
  TemplateAIAttributes,
  PropertyDetailsResponse,
  Similarities,
  ExpenseBenchmarksResponse
} from 'common'
import { defaultBestLeaseTermRentBasis, excelPayload } from 'common'
import imageCompression from 'browser-image-compression'

export default {
  name: 'GenerateReportDialog',
  components: {
    draggable
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    // Add props for the data needed to generate the report
    similarities: {
      type: Object as () => Record<string, Similarities>,
      default: () => ({})
    },
    subjectDetail: {
      type: Object as () => PropertyDetailsResponse | null,
      default: null
    },
    comparableDetails: {
      type: Array as () => PropertyDetailsResponse[],
      default: () => []
    },
    expenseBenchmarkData: {
      type: Object as () => ExpenseBenchmarksResponse | null,
      default: null
    },
    reportName: {
      type: String,
      default: ''
    },
    hasFinancialAnalysis: {
      type: Boolean,
      default: false
    },
    mapSnapshotUrl: {
      type: String,
      default: ''
    },
    mapSnapshotAlternativeUrl: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      mode: 'template' as 'template' | 'custom',
      templates: [] as Pick<TemplateAIAttributes, 'id' | 'name' | 'tags'>[],
      selectedTemplate: null as Pick<TemplateAIAttributes, 'id' | 'name' | 'tags'> | null,
      customSections: REPORT_SECTIONS.map((s) => ({ ...s, selected: false })),
      attachmentsFiles: [] as File[],
      loading: false,
      generating: false,
      error: '',
      reportContent: ''
    }
  },
  computed: {
    show: {
      get() {
        return this.modelValue
      },
      set(value: boolean) {
        this.$emit('update:modelValue', value)
      }
    },
    hasSimilarities() {
      return Object.keys(this.similarities).length > 0
    },
    selectedSectionsCount() {
      return this.customSections.filter((s) => s.selected).length
    },
    editorDefinitions() {
      return {
        print_with_images: {
          tip: 'Print',
          icon: 'print',
          handler: () => {
            const printWindow = window.open('', '_blank')
            if (!printWindow) {
              return
            }
            printWindow.document.write(`
              <html><head><title>Print Report</title>
              <style>body { font-family: Arial, sans-serif; padding: 20px; } img { max-width: 100%; }</style>
              </head><body>${this.reportContent}</body></html>
            `)
            printWindow.document.close()
            const images = printWindow.document.images
            if (images.length === 0) {
              printWindow.print()
              return
            }
            let loaded = 0
            const onReady = () => {
              loaded++
              if (loaded >= images.length) {
                printWindow.print()
              }
            }
            for (const img of images) {
              if (img.complete) {
                onReady()
              } else {
                img.addEventListener('load', onReady)
                img.addEventListener('error', onReady)
              }
            }
          }
        }
      }
    },
    hasExcelAttachments() {
      return this.attachmentsFiles.some((file) =>
        [
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-excel',
          'application/vnd.ms-excel.sheet.macroEnabled.12'
        ].includes(file.type)
      )
    }
  },
  created() {
    this.loadTemplates()
  },
  methods: {
    async loadTemplates() {
      this.loading = true
      this.error = ''

      try {
        this.templates = await templater.getAllReportTemplates()
      } catch (err) {
        console.error('Failed to load templates:', err)
        this.error = 'Failed to load templates. Please try again.'
      } finally {
        this.loading = false
      }
    },

    async generate() {
      if (this.mode === 'template' && !this.selectedTemplate) {
        return
      }
      if (this.mode === 'custom' && this.selectedSectionsCount === 0) {
        return
      }

      this.generating = true

      try {
        // Convert attachment files to Base64 before sending
        const attachments: { data: string; mimeType: string }[] = []
        for (const file of this.attachmentsFiles) {
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
              const result = reader.result as string
              resolve(result.split(',')[1])
            }
            reader.onerror = reject
            reader.readAsDataURL(file)
          })
          attachments.push({
            data: base64,
            mimeType: file.type
          })
        }

        // Validate that we have the required data
        if (!this.subjectDetail) {
          throw new Error('Subject property data is required to generate a report.')
        }

        if (this.comparableDetails.length === 0) {
          throw new Error('At least one comparable property is required to generate a report.')
        }

        // Create the same payload that's used for Excel export. For AI reports
        // we cap unit history and concessions to a trailing 24-month window.
        const payload = excelPayload(
          this.similarities,
          this.subjectDetail,
          this.comparableDetails,
          this.expenseBenchmarkData,
          useUserStore().organization?.best_lease_term_rent_basis === 'asking'
            ? 'asking'
            : defaultBestLeaseTermRentBasis,
          24
        )

        if (payload === null) {
          throw new Error(
            'Unable to generate payload. Please ensure all required data is available.'
          )
        }

        // Set the same parameters as Excel export
        const useDevFile = false
        const deletedTabs = 'Intro,Tables'
        const hiddenTabs = !this.hasFinancialAnalysis
          ? 'Financial Analysis,Pro Forma Model'
          : undefined

        let reportContent = ''
        let reportName = this.reportName
        if (!reportName) {
          reportName = this.subjectDetail?.name
            ? `${this.subjectDetail.name} Custom Report`
            : 'Custom Report'
        }

        if (this.mode === 'template' && this.selectedTemplate) {
          // Call the generateReport function with the template ID and payload
          reportContent = await templater.generateReport(
            this.selectedTemplate.id,
            payload,
            this.selectedTemplate.name,
            useDevFile,
            hiddenTabs,
            deletedTabs,
            attachments
          )
        } else if (this.mode === 'custom') {
          // Call the generateCustomReport function with selected sections (keys or image objects) and payload
          const selectedSections: (string | { type: 'image'; data: string; mimeType: string })[] =
            []
          for (const s of this.customSections.filter((s) => s.selected)) {
            const section = s as any
            if (section.type === 'image') {
              selectedSections.push({
                type: 'image',
                data: section.data,
                mimeType: section.mimeType
              })
            } else if (s.key === 'google_maps_image_static') {
              if (!this.mapSnapshotUrl) {
                this.$q.notify({
                  type: 'warning',
                  message: 'Google Maps image is not available yet. Skipping map section.'
                })
                continue
              }
              try {
                const mapImage = await this.fetchMapImageData(this.mapSnapshotUrl)
                selectedSections.push(mapImage)
              } catch (err) {
                console.error('Failed to fetch Google Maps image:', err)
                this.$q.notify({
                  type: 'warning',
                  message: 'Failed to load Google Maps image. Skipping map section.'
                })
                continue
              }
            } else if (s.key === 'google_maps_image_alternative') {
              if (!this.mapSnapshotAlternativeUrl) {
                this.$q.notify({
                  type: 'warning',
                  message:
                    'Alternative Google Maps image is not available yet. Skipping map section.'
                })
                continue
              }
              try {
                const mapImage = await this.fetchMapImageData(this.mapSnapshotAlternativeUrl)
                selectedSections.push(mapImage)
              } catch (err) {
                console.error('Failed to fetch alternative Google Maps image:', err)
                this.$q.notify({
                  type: 'warning',
                  message: 'Failed to load alternative Google Maps image. Skipping map section.'
                })
                continue
              }
            } else {
              selectedSections.push(s.key)
            }
          }

          reportContent = await templater.generateCustomReport(
            selectedSections,
            payload,
            reportName,
            useDevFile,
            hiddenTabs,
            deletedTabs,
            attachments
          )
        }

        // Handle the generated report content (markdown text)
        this.$q.notify({
          type: 'positive',
          message:
            this.mode === 'template'
              ? `Report generated successfully using template: ${this.selectedTemplate?.name}`
              : 'Custom report generated successfully'
        })

        // Track the successful report generation
        if (this.mode === 'template' && this.selectedTemplate) {
          this.$tracker.generatedReportFromDialog(
            this.selectedTemplate.id.toString(),
            this.selectedTemplate.name
          )
        } else {
          let reportName = this.reportName
          if (!reportName) {
            reportName = this.subjectDetail?.name
              ? `${this.subjectDetail.name} Custom Report`
              : 'Custom Report'
          }
          this.$tracker.generatedReportFromDialog('custom', reportName)
        }

        this.reportContent = reportContent
      } catch (err) {
        console.error('Failed to generate report:', err)
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to generate report. Please try again.'
        this.$q.notify({
          type: 'negative',
          message: errorMessage
        })
      } finally {
        this.generating = false
      }
    },

    cancel() {
      this.show = false
      this.reportContent = ''
      this.attachmentsFiles = []
    },
    onAttachmentRejected(rejectedEntries: any[]) {
      rejectedEntries.forEach((entry) => {
        if (entry.failedPropValidation === 'max-file-size') {
          this.$q.notify({
            type: 'negative',
            message: `File ${entry.file.name} is too large (max 500KB)`
          })
        } else if (entry.failedPropValidation === 'max-files') {
          this.$q.notify({
            type: 'negative',
            message: 'Maximum 3 files allowed'
          })
        }
      })
    },
    triggerImageUpload() {
      ;(this.$refs.imageInput as HTMLInputElement).click()
    },
    onImageSelected(event: Event) {
      const target = event.target as HTMLInputElement
      if (target.files && target.files[0]) {
        const file = target.files[0]
        const reader = new FileReader()
        reader.onload = async () => {
          const mimeType = file.type

          try {
            // Compress image to under 512KB
            const compressedFile = await imageCompression(file, {
              maxSizeMB: 0.5,
              maxWidthOrHeight: 768,
              useWebWorker: true,
              fileType: mimeType
            })

            const compressedFileBase64Encoded =
              await imageCompression.getDataUrlFromFile(compressedFile)

            const base64Data = compressedFileBase64Encoded.split(',')[1]

            const key = `image-${Date.now()}`
            this.customSections.unshift({
              key,
              label: `Image: ${file.name}`,
              prompt: '',
              selected: true,
              type: 'image',
              data: base64Data,
              mimeType: compressedFile.type
            } as any)
          } catch (error) {
            console.error('Failed to compress image:', error)
            this.$q.notify({
              type: 'negative',
              message: 'Failed to process image. Please try another one.'
            })
          }

          // Reset input
          target.value = ''
        }
        reader.readAsDataURL(file)
      }
    },
    removeImage(element: any) {
      const index = this.customSections.findIndex((s) => s.key === element.key)
      if (index !== -1) {
        this.customSections.splice(index, 1)
      }
    },
    async fetchMapImageData(
      url: string
    ): Promise<{ type: 'image'; data: string; mimeType: string }> {
      const response = await fetch(url)
      const blob = await response.blob()
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const result = reader.result as string
          resolve(result.split(',')[1] ?? '')
        }
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
      return {
        type: 'image',
        data: base64,
        mimeType: blob.type || 'image/png'
      }
    }
  }
}
</script>
